import '../styles/global.css';

import type { AnalyticsData } from '@pickthebank/api-sdk/type/request/auth/login.dto';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import App from 'next/app';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { fileUrlToUrl, withTranslateRoutes } from 'next-translate-routes';
import { useRouter } from 'next-translate-routes/router';
import NextNProgress from 'nextjs-progressbar';
import { parseCookies, setCookie } from 'nookies';
import { useEffect } from 'react';

import { Cookies, DialogAuthorization, Sprite } from '@/components';
import type { IRawFooterLink } from '@/components/Footer/footer.types';
import { useDocumentHeight } from '@/components/SearchBar/parts/hooks/useDocumentHeight';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { ApiProvider } from '@/contexts/ApiContext';
import type { SeoContent } from '@/contexts/CmsContentContext';
import { CmsContentProvider } from '@/contexts/CmsContentContext';
import { ModalProvider } from '@/contexts/ModalContext';
import { useGtagRouterEvents } from '@/hooks/useGtagRouterEvents';
import { fetchFooterPages, fetchSlugPage } from '@/services/supabase';
import {
  useAuthModalsControl,
  useAuthStore,
  useCookiesStore,
  useSearchStore,
} from '@/store';
import { useBestOffersModalStore } from '@/store/bestOffersModalStore';
import { prepareToLocalizations, useI18nStore } from '@/store/storeI18n';
import { saveUTMParams } from '@/utils';
import { isMobileApp } from '@/utils/isMobileApp';
import type { UserCountry } from '@/utils/nextjs';
import { getUserCountry, getUserExactCountryCode } from '@/utils/nextjs';

const FB_PIXEL_ID = process.env.NEXT_PUBLIC_PIXEL || '';
const GOOGLE_ANALYTICS = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS || '';

type TProps = AppProps & {
  analytics: AnalyticsData;
  footerLinks: IRawFooterLink[];
  seoContent: SeoContent;
  country: UserCountry;
  exactCountryCode?: string;
  hasViewedBestOffers: boolean;
};

const prepareUrlToFetching = (url: string) => {
  if (url === '/') return url;
  const cleanUrl = url.split('?')[0] || '/';
  return cleanUrl.replace(/\/$/, '');
};

const MyApp = ({
  Component,
  pageProps,
  analytics,
  footerLinks,
  seoContent,
  country,
  exactCountryCode,
  hasViewedBestOffers,
}: TProps) => {
  const router = useRouter();
  const resetLocalizations = useI18nStore((state) => state.resetLocalizations);
  const marketingCookies = useCookiesStore((state) => state.marketingCookies);
  const setMarketingCookies = useCookiesStore(
    (state) => state.setMarketingCookies,
  );
  const { setCountry, setCurrency } = useSearchStore((state) => state);
  const { query } = useRouter();

  const {
    setIsTestUser,
    userUuid,
    hash: userHash,
    fullName,
    email,
  } = useAuthStore((state) => state);
  const { openSignupModal } = useAuthModalsControl((state) => state);

  const { setIsModalShown: setIsBestOffersModalShown } =
    useBestOffersModalStore((state) => state);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBestOffersModalShown(hasViewedBestOffers);
    }
    if (typeof window !== 'undefined' && analytics) {
      if (
        router.query?.test_user === 'true' ||
        router.query?.impersonate_token
      ) {
        setIsTestUser(true);
      }
      if (router.query?.test_user === 'false') {
        setIsTestUser(false);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const tawkApiReady = () => {
        // Login when the Tawk.to widget is ready
        // @ts-ignore
        window.TAWK_API?.login({ hash: userHash, userId: userUuid });
      };

      // @ts-ignore
      window.Tawk_API = window.Tawk_API || {};
      // @ts-ignore
      window.Tawk_API.onLoad = tawkApiReady;
      // @ts-ignore
      window.Tawk_API.visitor = {
        name: fullName,
        email,
        hash: userHash,
      };
    }
  }, [userUuid, userHash, fullName, email]);

  useEffect(() => {
    if (!query.country) {
      setCountry(country.name);
      if (!query.currency) {
        setCurrency(country.currency);
      }
    }
  }, [country]);

  useGtagRouterEvents();

  useEffect(() => {
    setMarketingCookies(
      Boolean(localStorage.getItem('marketing-cookies') === 'true'),
    );
  }, []);

  useDocumentHeight();

  useEffect(() => {
    window.onpopstate = (e) => {
      router.push(e.state.url);
    };
  });

  useEffect(() => {
    if (router.asPath.includes('#auth')) {
      openSignupModal();
      router.replace(router.asPath.replace('#auth', ''), undefined, {
        scroll: false,
      });
    }
  }, [router]);

  useEffect(() => {
    // We need this for proper usage with storeI18n.tsx scenario.
    // When changing pages, it is necessary to reset the language selectors' localization status.
    router.events.on('routeChangeStart', () => {
      resetLocalizations();
    });

    // Facebook Pixel
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.init(FB_PIXEL_ID);
        ReactPixel.pageView();

        router.events.on('routeChangeComplete', () => {
          ReactPixel.pageView();
        });
      });

    saveUTMParams();
  }, []);

  return (
    <ApiProvider>
      <Script
        strategy="lazyOnload"
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS}`}
      />
      <Script id="google_analytics" strategy="lazyOnload">
        {`
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${GOOGLE_ANALYTICS}');

                    ${
                      marketingCookies &&
                      `
                        window['ga-disable-${GOOGLE_ANALYTICS}'] = false;
                      `
                    } 
        `}
      </Script>
      {!isMobileApp() && (
        <Script id="tawk-chat" type="text/javascript" strategy="lazyOnload">
          {`
        var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
        (function(){
          var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
          s1.async=true;
          s1.src='https://embed.tawk.to/66179c911ec1082f04e1272b/1hr63n6mf';
          s1.charset='UTF-8';
          s1.setAttribute('crossorigin','*');
          s0.parentNode.insertBefore(s1,s0);
        })();
        `}
        </Script>
      )}

      {FB_PIXEL_ID !== '' &&
        FB_PIXEL_ID !== 'tobechanged' &&
        marketingCookies && (
          <noscript>
            <img
              height="1"
              width="1"
              alt="fb_pixel"
              style={{ display: 'none' }}
              src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
            />
          </noscript>
        )}

      <Sprite />
      <NextNProgress
        color="#3875FC"
        startPosition={0.0}
        stopDelayMs={200}
        height={3}
      />
      <AnalyticsProvider analytics={analytics}>
        <CmsContentProvider
          footerLinks={footerLinks}
          seoContent={seoContent}
          country={country}
          exactCountryCode={exactCountryCode}
        >
          <Cookies />
          <DialogAuthorization />
          <ModalProvider>
            <Component {...pageProps} />
          </ModalProvider>
        </CmsContentProvider>
      </AnalyticsProvider>
    </ApiProvider>
  );
};

type AppOwnProps = {
  analytics: AnalyticsData;
  footerLinks: IRawFooterLink[];
  seoContent: SeoContent;
  country: UserCountry;
  exactCountryCode?: string;
  hasViewedBestOffers: boolean;
};

MyApp.getInitialProps = async (
  context: AppContext,
): Promise<AppOwnProps & AppInitialProps> => {
  const { query } = context.router;
  let tracking_type: string | undefined;
  if (context?.router.query?.fbclid) {
    tracking_type = 'facebook';
  }
  if (context?.router?.query?.gclid) {
    tracking_type = 'google';
  }
  let analytics: any = {
    referer: context?.ctx?.req?.headers.referer,
    utm_source: context?.router.query?.utm_source as string,
    utm_medium: context?.router.query?.utm_medium as string,
    utm_campaign: context?.router.query?.utm_campaign as string,
    fbclid: context?.router.query?.fbclid as string,
    gclid: context?.router?.query?.gclid as string,
    subid: context?.router?.query?.subid as string,
    tracking_type,
  };
  const ctx = await App.getInitialProps(context);

  const cookies = parseCookies({ req: context?.ctx?.req });

  if (!cookies?.has_viewed_best_offers) {
    context?.ctx?.res?.setHeader('Set-Cookie', 'has_viewed_best_offers=true');
  }

  const utms = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  if (query.s_id && query.utm_source !== 'kritische-anleger') {
    const host = context?.ctx?.req?.headers.host || 'pickthebank.eu';
    const domain = host.includes(':') ? host.split(':')[0] : host;

    setCookie(context.ctx, 's_id', String(query.s_id), {
      path: '/',
      domain,
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      maxAge: 60 * 60 * 24 * 90, // 90 days
    });
    setCookie(context.ctx, 'fasource', String(query.fasource), {
      path: '/',
      domain,
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
    });
    utms.forEach((utm) => {
      setCookie(context.ctx, utm, '', {
        path: '/',
        domain,
        httpOnly: true,
        secure: true,
        sameSite: 'lax',
        maxAge: -1, // 90 days
      });
    });
    analytics = {
      s_id: query.s_id,
      fasource: query.fasource,
    };
  } else if (
    !query.s_id &&
    (query.utm_source ||
      query.utm_medium ||
      query.utm_campaign ||
      query.utm_term ||
      query.utm_content)
  ) {
    const host = context?.ctx?.req?.headers.host || 'pickthebank.eu';
    const domain = host.includes(':') ? host.split(':')[0] : host;

    utms.forEach((utm) => {
      setCookie(context.ctx, utm, String(query[utm]), {
        path: '/',
        domain,
        httpOnly: true,
        secure: true,
        sameSite: 'lax',
        maxAge: query[utm] ? 60 * 60 * 24 * 90 : -1, // 90 days
      });
    });
    setCookie(context.ctx, 's_id', '', {
      path: '/',
      domain,
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      maxAge: -1,
    });
    setCookie(context.ctx, 'fasource', '', {
      path: '/',
      domain,
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      maxAge: -1,
    });
  }

  try {
    const { data: footerLinks } = await fetchFooterPages(
      context.ctx.locale || 'en',
    );

    const locale = context.ctx.locale || 'en';

    const path = fileUrlToUrl(
      {
        pathname: context.router.pathname,
      },
      locale,
      { throwOnError: false },
    );

    const localSlug = prepareUrlToFetching(
      path ||
        (locale === 'en'
          ? context.ctx.asPath || '/'
          : `/${locale}${context.ctx.asPath}`),
    );

    const fetchedPages = await fetchSlugPage(localSlug, locale);

    const cmsPage = fetchedPages?.data[0];

    const country = getUserCountry(context.ctx);
    const exactCountryCode = getUserExactCountryCode(context.ctx);

    let localizations = cmsPage?.attributes?.localizations?.data;

    if (localizations) {
      localizations = prepareToLocalizations(
        cmsPage.attributes.localizations.data,
        'link',
        '',
        locale,
        locale,
      );
    }

    return {
      ...ctx,
      analytics,
      footerLinks,
      seoContent: {
        title: cmsPage?.attributes?.title || '',
        description: cmsPage?.attributes?.description || '',
        mainText: cmsPage?.attributes?.Body || '',
        metaImage: cmsPage?.attributes?.seo?.metaImage?.data?.attributes?.url
          ? `/cms${cmsPage?.attributes?.seo?.metaImage?.data?.attributes?.url}`
          : '',
        keywords: cmsPage?.attributes?.seo?.keywords || '',
        canonical: cmsPage?.attributes?.seo?.canonicalURL || '',
        localizations,
      },
      country,
      exactCountryCode,
      hasViewedBestOffers: cookies.has_viewed_best_offers === 'true',
    };
  } catch (e) {
    return {
      ...ctx,
      analytics,
      footerLinks: [],
      seoContent: {
        title: '',
        description: '',
        mainText: '',
        metaImage: '',
        keywords: '',
        canonical: '',
        localizations: undefined,
      },
      country: {
        name: 'Germany',
        currency: 'eur',
        alpha2: 'DE',
      },
      hasViewedBestOffers: cookies.has_viewed_best_offers === 'true',
    };
  }
};
export default appWithTranslation(withTranslateRoutes(JSON.parse(`{"defaultLocale":"en","locales":["en","de","nl","es","it","fr","pt","el","sv","lt","fi"],"routesTree":{"name":"","paths":{"default":"","en":"","de":"","nl":"","es":"","it":"","fr":"","pt":"","el":"","sv":"","lt":"","fi":""},"children":[{"name":"403","paths":{"default":"403"}},{"name":"admin","paths":{"default":"admin"}},{"name":"demo-mobile","paths":{"default":"demo-mobile"}},{"name":"demo","paths":{"default":"demo"}},{"name":"index","paths":{"default":"index"}},{"name":"lead-generation-demo","paths":{"default":"lead-generation-demo"}},{"name":"robots","paths":{"default":"robots"}},{"name":"about-us","paths":{"default":"about-us","en":"about-us","de":"uber-uns","nl":"over-ons","es":"sobre-nosotros","it":"chi-siamo","fr":"a-propos-de-nous","pt":"sobre-nos","el":"peri-mas","sv":"om-oss","lt":"apie-mus","fi":"tietoa-meista"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"banks","paths":{"default":"banks","en":"banks","de":"banken","nl":"banken","es":"bancos","it":"banche","fr":"banques","pt":"bancos","el":"trapezes","sv":"banker","lt":"bankai","fi":"pankit"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[country]","paths":{"default":":country"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]}]},{"name":"blog","paths":{"default":"blog"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"brokers","paths":{"default":"brokers"},"children":[{"name":"freedom24","paths":{"default":"freedom24"}}]},{"name":"deposits","paths":{"default":"deposits"},"children":[{"name":"[productIndex]","paths":{"default":":productIndex"},"children":[{"name":"apply","paths":{"default":"apply"}},{"name":"index","paths":{"default":"index"}}]}]},{"name":"favourites","paths":{"default":"favourites","en":"favourites","de":"favoriten","nl":"favorieten","es":"favoritos","it":"preferiti","fr":"favoris","pt":"favoritos","el":"agapimena","sv":"favoriter","lt":"megstamiausi","fi":"suosikit"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"fixed-deposits","paths":{"default":"fixed-deposits","en":"fixed-deposits","de":"festgeld","nl":"depositos","es":"deposito-a-plazo-fijo","it":"conti-depositi","fr":"comptes-de-depots","pt":"depositos-a-prazo-fixos","el":"statheres-katastaseis","sv":"fast-ranta","lt":"fiksuotos-lesos","fi":"kiinteakorkoiset-talletukset"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[country]","paths":{"default":":country"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[term]","paths":{"default":":term"},"children":[{"name":"[currency]","paths":{"default":":currency"},"children":[{"name":"index","paths":{"default":"index"}}]}]}]}]},{"name":"fixed-deposits-in-germany","paths":{"default":"fixed-deposits-in-germany","en":"fixed-deposits-in-germany","de":"festgeld-in-deutchland","nl":"spaarrekeningen-in-duitsland","es":"cuentas-de-ahorro-en-alemania","it":"conti-di-deposito-in-germania","fr":"comptes-depargne-en-allemagne","pt":"contas-de-poupanca-na-alemanha","el":"logariasmoi-apotamieyshs-sth-germania","sv":"sparkonton-i-tyskland","lt":"taupymo-saskaitos-vokietijoje","fi":"saastotilit-saksassa"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"landings","paths":{"default":"landings"},"children":[{"name":"bluor","paths":{"default":"bluor"},"children":[{"name":"index","paths":{"default":"index"}}]}]},{"name":"lidion","paths":{"default":"lidion","en":"lidion","de":"lidion","nl":"lidion","es":"lidion","it":"lidion","fr":"lidion","pt":"lidion","el":"lidion","sv":"lidion","lt":"lidion","fi":"lidion"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"mobile-app","paths":{"default":"mobile-app"},"children":[{"name":"faq","paths":{"default":"faq"}},{"name":"index","paths":{"default":"index"}}]},{"name":"multibanking-platform","paths":{"default":"multibanking-platform"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"profile","paths":{"default":"profile"},"children":[{"name":"faq","paths":{"default":"faq"}},{"name":"index","paths":{"default":"index"}},{"name":"applications","paths":{"default":"applications"},"children":[{"name":"[id]","paths":{"default":":id"},"children":[{"name":"details","paths":{"default":"details"}},{"name":"documents","paths":{"default":"documents"}},{"name":"index","paths":{"default":"index"}},{"name":"overview","paths":{"default":"overview"}},{"name":"payments","paths":{"default":"payments"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"manual","paths":{"default":"manual"}}]}]}]},{"name":"compliance","paths":{"default":"compliance"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"notifications","paths":{"default":"notifications"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"portfolio","paths":{"default":"portfolio"},"children":[{"name":"archive","paths":{"default":"archive"}},{"name":"index","paths":{"default":"index"}}]}]},{"name":"redirect","paths":{"default":"redirect"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"savings-accounts","paths":{"default":"savings-accounts","en":"savings-accounts","de":"sparkonten","nl":"spaarrekeningen","es":"cuentas-de-ahorro","it":"conti-di-risparmio","fr":"comptes-d-epargne","pt":"contas-de-poupanca","el":"logariasmoi-soson","sv":"sparkonton","lt":"taupymo-saskaitos","fi":"saastotilit"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[country]","paths":{"default":":country"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[currency]","paths":{"default":":currency"},"children":[{"name":"[annualFee]","paths":{"default":":annualFee"},"children":[{"name":"[cashback]","paths":{"default":":cashback"},"children":[{"name":"index","paths":{"default":"index"}}]}]}]}]}]},{"name":"statistics","paths":{"default":"statistics","en":"statistics","de":"statistik","nl":"statistieken","es":"estadisticas","it":"statistiche","fr":"statistiques","pt":"estatisticas","el":"statistika","sv":"statistik","lt":"statistika","fi":"tilastot"},"children":[{"name":"maximum-interest-rates-in-different-countries","paths":{"default":"maximum-interest-rates-in-different-countries","en":"maximum-interest-rates-in-different-countries","de":"hochstzinssatze-in-verschiedenen-landern","nl":"maximale-rentetarieven-in-verschillende-landen","es":"tasas-de-interes-maximas-en-diferentes-paises","it":"tassi-di-interesse-massimi-in-diversi-paesi","fr":"taux-dinteret-maximum-dans-differents-pays","pt":"taxas-de-juro-maximas-em-diferentes-paises","el":"maksima-diafora-epitokia-se-diafora-chora","sv":"maximala-rantorna-i-olika-lander","lt":"maksimalios-pasiskolintos-sumos-skyriuose-salies","fi":"maksimi-korkoja-eri-maissa"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[term]","paths":{"default":":term"},"children":[{"name":"[currency]","paths":{"default":":currency"},"children":[{"name":"index","paths":{"default":"index"}}]}]}]},{"name":"savings-accounts","paths":{"default":"savings-accounts","en":"savings-accounts","de":"sparkonten","nl":"spaarrekening","es":"guardando-cuentas","it":"conti-di-risparmio","fr":"comptes-depargne","pt":"contas-de-poupanca","el":"logariasmoi-soson","sv":"spar-konton","lt":"taupymo-saskaitos","fi":"saastotilit"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[currency]","paths":{"default":":currency"},"children":[{"name":"index","paths":{"default":"index"}}]}]}]},{"name":"[...slug]","paths":{"default":":slug+"}}]},"fallbackLng":{}}`), MyApp));
